import * as Yup from "yup";
import validator from "validator";
import * as i18next from "i18next";

import { ExtensionField, LookupField } from "types/payable";
import { getLabel } from "utilities/helper";
import { getFormattedTime } from "./utils";

// TODO: change to libphonenumber-js library as in MyHQ+.
function validateMobileNumber(value: string | null | undefined) {
  return value ? validator.isMobilePhone(value, "en-US") : true;
}

export function generateValidationSchema(
  fields: LookupField[] | ExtensionField[],
  t: i18next.TFunction,
  labels: any
) {
  return Yup.object(
    (fields as any[]).reduce(
      (schema: Record<string, any>, field: LookupField | ExtensionField) => {
        // All lookup fields are required, only extension fields can be optional.
        let isRequired = true;

        if ("optionalForPayment" in field) {
          isRequired = !field.optionalForPayment;
        }

        // TODO: the error messages should come from Phrasebook.

        if (field.type === "NonblankText") {
          let fieldSchema = Yup.string();

          if (isRequired) {
            fieldSchema = fieldSchema.required(
              `${t("nonblanktext-required-error", {
                fieldName: getLabel(labels, field.name),
              })}`
            );
          }

          if (field.minLength) {
            fieldSchema = fieldSchema.min(
              field.minLength,
              t("nonblanktext-min-length-error", {
                fieldName: getLabel(labels, field.name),
                minLength: field.minLength,
              })
            );
          }

          if (field.maxLength) {
            fieldSchema = fieldSchema.max(
              field.maxLength,
              `${t("nonblanktext-max-length-error", {
                fieldName: getLabel(labels, field.name),
                maxLength: field.maxLength,
              })}`
            );
          }

          schema[field.name] = fieldSchema;
        } else if (field.type === "Address") {
          // TODO: The schema for Address should be an object because it has multiple input fields.

          let fieldSchema = Yup.string();
          if (isRequired) {
            fieldSchema = fieldSchema.required(
              `${t("address-required-error", {
                fieldName: getLabel(labels, field.name),
              })}`
            );
          }
          schema[field.name] = fieldSchema;
        } else if (field.type === "Integer") {
          let fieldSchema = Yup.number();

          if (isRequired) {
            fieldSchema = fieldSchema.required(
              `${t("integer-required-error", {
                fieldName: getLabel(labels, field.name),
              })}`
            );
          }

          if (field.min) {
            fieldSchema = fieldSchema.min(
              field.min,
              t("integer-min-error", {
                fieldName: getLabel(labels, field.name),
                minLength: field.min,
              })
            );
          }

          if (field.max) {
            fieldSchema = fieldSchema.max(
              field.max,
              `${t("integer-max-error", {
                fieldName: getLabel(labels, field.name),
                maxLength: field.max,
              })}`
            );
          }

          schema[field.name] = fieldSchema;
        } else if (field.type === "DollarAmount") {
          let fieldSchema = Yup.number();

          if (isRequired) {
            fieldSchema = fieldSchema.required(
              `${t("dollaramount-required-error", {
                fieldName: getLabel(labels, field.name),
              })}`
            );
          }

          if (field.min) {
            fieldSchema = fieldSchema.min(
              field.min,
              t("dollaramount-min-error", {
                fieldName: getLabel(labels, field.name),
                min: field.min,
              })
            );
          }

          if (field.max) {
            fieldSchema = fieldSchema.max(
              field.max,
              `${t("dollaramount-max-error", {
                fieldName: getLabel(labels, field.name),
                max: field.max,
              })}`
            );
          }

          schema[field.name] = fieldSchema;
        } else if (field.type === "Boolean") {
          let fieldSchema = Yup.boolean();

          if (isRequired) {
            fieldSchema = fieldSchema.required(
              `${t("boolean-required-error", {
                fieldName: getLabel(labels, field.name),
              })}`
            );
          }

          schema[field.name] = fieldSchema;
        } else if (field.type === "Email") {
          let fieldSchema = Yup.string().email(
            `${t("email-invalid-error", {
              fieldName: getLabel(labels, field.name),
            })}`
          );

          if (isRequired) {
            fieldSchema = fieldSchema.required(
              `${t("email-required-error", {
                fieldName: getLabel(labels, field.name),
              })}`
            );
          }

          schema[field.name] = fieldSchema;
        } else if (field.type === "PhoneNumber") {
          let fieldSchema = Yup.string().test(
            schema[field.name],
            `${t("phonenumber-invalid-error", {
              fieldName: getLabel(labels, field.name),
            })}`,
            (phoneNumber) => validateMobileNumber(phoneNumber)
          );

          if (isRequired) {
            fieldSchema = fieldSchema.required(
              `${t("phonenumber-required-error", {
                fieldName: getLabel(labels, field.name),
              })}`
            );
          }

          schema[field.name] = fieldSchema;
        } else if (field.type === "Date") {
          let fieldSchema = Yup.date();

          if (isRequired) {
            fieldSchema = fieldSchema.required(
              `${t("date-required-error", {
                fieldName: getLabel(labels, field.name),
              })}`
            );
          }

          if (field.min) {
            const minDate = new Date(field.min);
            fieldSchema = fieldSchema.min(
              minDate,
              t("date-min-error", {
                fieldName: getLabel(labels, field.name),
                min: field.min,
              })
            );
          }

          if (field.max) {
            const maxDate = new Date(field.max);
            fieldSchema = fieldSchema.max(
              maxDate,
              t("date-max-error", {
                fieldName: getLabel(labels, field.name),
                max: field.max,
              })
            );
          }

          schema[field.name] = fieldSchema;
        } else if (field.type === "DateTime") {
          let dateSchema = Yup.date().test(
            "conditional-required",
            `${t("datetime-date-required-error", {
              fieldName: getLabel(labels, field.name),
            })}`,
            function (value) {
              const { time } = this.parent;
              if (time && time.length > 0) {
                return value != null;
              }
              return true;
            }
          );

          let timeSchema = Yup.string()
            .matches(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/)
            .test(
              "conditional-required",
              `${t("datetime-time-required-error", {
                fieldName: getLabel(labels, field.name),
              })}`,
              function (value) {
                const { date } = this.parent;
                if (date != null) {
                  return Boolean(value && value.length > 0);
                }
                return true;
              }
            );

          if (isRequired) {
            dateSchema = dateSchema.required(
              `${t("datetime-date-required-error", {
                fieldName: getLabel(labels, field.name),
              })}`
            );
            timeSchema = timeSchema.required(
              `${t("datetime-time-required-error", {
                fieldName: getLabel(labels, field.name),
              })}`
            );
          }

          if (field.min) {
            const minDateTime = new Date(field.min);
            const minDateOnly = new Date(minDateTime.toDateString());
            const minTimeOnly = getFormattedTime(minDateTime);

            dateSchema = dateSchema.min(
              minDateOnly,
              t("datetime-date-min-error", {
                fieldName: getLabel(labels, field.name),
                min: minDateOnly,
              })
            );
            timeSchema = timeSchema.test(
              "min-time",
              t("datetime-time-min-error", {
                fieldName: getLabel(labels, field.name),
                min: minTimeOnly,
              }),
              (value) => (value ? value >= minTimeOnly : !isRequired)
            );
          }

          if (field.max) {
            const maxDateTime = new Date(field.max);
            const maxDateOnly = new Date(maxDateTime.toDateString());
            const maxTimeOnly = getFormattedTime(maxDateTime);

            dateSchema = dateSchema.max(
              maxDateOnly,
              t("datetime-date-max-error", {
                fieldName: getLabel(labels, field.name),
                max: maxDateOnly,
              })
            );

            timeSchema = timeSchema.test(
              "max-time",
              t("datetime-time-max-error", {
                fieldName: getLabel(labels, field.name),
                max: maxTimeOnly,
              }),
              (value) => (value ? value <= maxTimeOnly : !isRequired)
            );
          }

          schema[field.name] = Yup.object({
            date: dateSchema,
            time: timeSchema,
          });
        } else if (field.type === "SelectOne") {
          let fieldSchema = Yup.string();

          if (isRequired) {
            fieldSchema = fieldSchema.required(
              `${t("selectone-required-error", {
                fieldName: getLabel(labels, field.name),
              })}`
            );
          }

          schema[field.name] = fieldSchema;
        } else if (field.type === "SelectMany") {
          let fieldSchema = Yup.array().of(Yup.string());

          if (isRequired) {
            fieldSchema = fieldSchema
              .required(
                `${t("selectmany-required-error", {
                  fieldName: getLabel(labels, field.name),
                })}`
              )
              .min(
                1,
                `${t("selectmany-required-error", {
                  fieldName: getLabel(labels, field.name),
                })}`
              );
          }

          schema[field.name] = fieldSchema;
        } else {
          // Exhaustive check to make sure we handle all field types.
          throw field.type satisfies never;
        }

        return schema;
      },
      {}
    )
  );
}
